/* global newrelic */

import { lazy, Suspense, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Redirect, Router, Switch, Route } from "react-router-dom";
import { SnackbarProvider } from "notistack";

import { FaceOrgProvider } from "contexts/faceorg-context";
import { ExperimentProvider } from "contexts/experiment-context";
import { InAppBrowserIntentProvider } from "contexts/InAppBrowserIntentContext";
import "./global.css";
import UserLayout from "layouts/UserLayout";
import NoLayout from "layouts/NoLayout";
import ProtectedRoute from "components/ProtectedRoute";
import {
  MakeView,
  MakePreview,
  MorphablePackGroup,
  InvitesPreview,
  TemplateGroup,
  Categories,
  Category,
  Home,
  Login,
  NewCast,
  People,
  MyEcards,
  MyInvites,
  Overview,
  ChangeEmail,
  ChangeName,
  ChangePassword,
  Receipts,
  Receipt,
  SubscriptionDetails,
  EditRenewal,
  UpdateCreditCard,
  Legal,
  AboutUs,
  Privacy,
  TermsOfSale,
  TermsOfService,
  SignUp,
  ResetPassword,
  ResetPasswordReturn,
  LoginHelp,
  Payment,
  PaypalConfirmation,
  PurchaseConfirmation,
  Face,
  Search,
  Page404,
  Unauthorized,
  InviteCategory,
  InvitesCasting,
  InviteMakePreview,
  InviteGuestView,
  ManageInvite,
  InvitePublicView,
  InvitesLandingPage,
  StarringYouAILandingPage,
  SYAIPhotoConfirmation,
  SYAIPackPreview,
  SYAIPackView,
  SYAIMyPacks,
  AlgoliaTestPage,
} from "pages";
import ThemeProvider from "layouts/ThemeProvider";
import LoaderBricks from "components/LoaderBricks";
import RenderModal from "components/RenderModal";
import FeatureFlags from "components/FeatureFlags";
import history from "utils/history";
import localStorage, { LOCAL_STORAGE_AUTH_KEY, RECIPIENT_SESSION_KEY } from "utils/localStorage";
import { pageViewEvent, setupTrackingId, getTrackingId, setTrackingId } from "tracking/jibjab/tracking";
import { appRoutes } from "routes";
import {
  useUsers,
  useOffers,
  useRouter,
  useScrollToTopOnPathnameChange,
  useLoading,
  useDetectAdblock,
  usePageUpdated,
  useUtmsParams,
  useGA4,
  useRefreshAuthToken,
  useInFlowManager,
  useTrackAccountEvents,
} from "hooks";
import {
  storeTestEventCode,
  trackViewContent,
  trackPageView as trackFacebookPageView,
  setUpFacebookQueue,
} from "tracking/facebook/conversion";

import { jjLogger, MOBILE_DEBUG_COOKIE_NAME } from "./utils/logUtils";
import { registerServiceWorker } from "./serviceWorkerRegistration";

const Routes = () => {
  const [trackVisit, setTrackVisit] = useState(true);
  const [trackingID, setTrackingID] = useState(null);
  const { setCurrentOffer } = useOffers();
  const { currentUser, signOut, loadCurrentUser, setCurrentUserLoadingStatus } = useUsers();
  const { initializeValue, clearValue, initialValue } = useDetectAdblock();
  const {
    query: { mobileDebug = null },
    camelizedQuery: { trackingId: trackingIdFromUrl, landingDefaultBrowser },
    location,
  } = useRouter();
  const { func: loadUserSession, status: currentUserStatus, isFinished: isUserSessionLoaded } = useLoading(
    loadCurrentUser,
  );
  const { handleUtms } = useUtmsParams();
  const { trackLandingDefaultBrowserEvents } = useTrackAccountEvents();

  const syncLoginAcrossTabs = (e) => {
    if (window.forceRefresh) return;

    if (e.key === LOCAL_STORAGE_AUTH_KEY) {
      if (e.oldValue && !e.newValue) {
        jjLogger.logDebug("UserApp.js: LocalStorage Change Event: Signing Out");
        signOut(true);
      } else {
        jjLogger.logDebug("UserApp.js: LocalStorage Change Event: Refreshing Page");
        window.forceRefresh = true;
        window.location.reload();
      }
    }
  };

  const logErrors = (e) => {
    jjLogger.logError(e.message);
  };

  const logUnhandledRejection = (e) => {
    jjLogger.logError(`Unhandled Promise Rejection: ${e.reason}`);
  };

  const initializeTrackingID = () => {
    jjLogger.log(`UserApp.js: initializeTrackingID() with trackingIdFromUrl: ${trackingIdFromUrl}`);
    setupTrackingId(trackingIdFromUrl);
    setTrackingID(getTrackingId());
  };

  const reSaveTrackingID = () => {
    const current = getTrackingId();
    jjLogger.log(`UserApp.js: getTrackingId(): ${current} vs trackingID: ${trackingID}`);

    if (!current) {
      jjLogger.log("UserApp.js: reSaveTrackingID(): setting tracking id");
      setTrackingId(trackingID);
    }
  };

  useScrollToTopOnPathnameChange();
  useGA4();
  useInFlowManager();

  useEffect(() => {
    window.addEventListener("storage", syncLoginAcrossTabs);
    window.addEventListener("error", logErrors);
    window.addEventListener("unhandledrejection", logUnhandledRejection);

    return () => {
      window.removeEventListener("storage", syncLoginAcrossTabs);
      window.removeEventListener("error", logErrors);
      window.removeEventListener("unhandledrejection", logUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    storeTestEventCode();
    initializeTrackingID();
    loadUserSession();
    setUpFacebookQueue();
  }, []);

  useEffect(() => {
    initializeValue();
    return () => {
      clearValue();
    };
  }, [initialValue]);

  useEffect(() => {
    setCurrentOffer();
    handleUtms();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("referrerUrl")) {
      sessionStorage.setItem("referrerUrl", sessionStorage.getItem("currentUrl"));
    } else {
      sessionStorage.setItem("referrerUrl", document.referrer || "direct");
    }

    sessionStorage.setItem("currentUrl", window.location.href);
  }, [location]);

  useEffect(() => {
    const path = location.pathname + location.search;
    const isViewMakePage = /^\/view\/make\/*\/*\//.test(path);
    if (!localStorage.hasItem(RECIPIENT_SESSION_KEY) && !isViewMakePage) {
      localStorage.setItem(RECIPIENT_SESSION_KEY, "no");
    }
  }, []);

  useEffect(() => {
    if (mobileDebug === "on") {
      Cookies.set(MOBILE_DEBUG_COOKIE_NAME, true);
    } else if (mobileDebug === "off") {
      Cookies.remove(MOBILE_DEBUG_COOKIE_NAME);
    }
  }, [mobileDebug]);

  useEffect(() => {
    setCurrentUserLoadingStatus(currentUserStatus);
  }, [currentUserStatus]);

  useEffect(() => {
    if (trackVisit && isUserSessionLoaded) {
      setTrackVisit(false);
      reSaveTrackingID();
      pageViewEvent(currentUser);
      if (landingDefaultBrowser) trackLandingDefaultBrowserEvents(currentUser, landingDefaultBrowser);
    }
  }, [isUserSessionLoaded]);

  useEffect(() => {
    if (currentUser && typeof newrelic !== "undefined") {
      newrelic.setCustomAttribute("userId", currentUser.id);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!isUserSessionLoaded) return;

    reSaveTrackingID();

    const path = location.pathname + location.search;
    const isHomePage = path === "/";
    const isCategoryPage = /^(\/invites)?\/browse\/.+$/.test(path);
    const isTemplateGroupPage = /^\/(view\/template|invites\/view\/template)\/.+$/.test(path);
    const skipPath =
      isHomePage ||
      /^\/not-found/.test(path) ||
      isTemplateGroupPage ||
      /^\/view\/make\/*\/*\//.test(path) ||
      /^\/view\/make-preview\/*\/*\//.test(path) ||
      /^\/face/.test(path) ||
      /^\/search(\/)?(\?.+)+/.test(path);

    if (!isHomePage && !isCategoryPage && !isTemplateGroupPage) window.prerenderReady = true;

    trackFacebookPageView(currentUser);

    if (skipPath) return;

    if (/^\/create\/account/.test(path)) {
      trackViewContent(currentUser, { content_name: "No Account Creation" });
    } else if (/^\/create\/cast\/.+$/.test(path)) {
      trackViewContent(currentUser, { content_name: "Cast More Faces" });
    }
  }, [location, isUserSessionLoaded]);

  return (
    <Suspense fallback={<LoaderBricks />}>
      <FeatureFlags trackingID={trackingID}>
        <Switch>
          <Route exact path={[appRoutes.searchPath()]}>
            <NoLayout>
              <Switch>
                <Route path={appRoutes.searchPath()} component={Search} />
              </Switch>
            </NoLayout>
          </Route>
          <Route path=":path?">
            <UserLayout>
              <Switch>
                <Route exact path={appRoutes.rootPath()}>
                  <Home />
                </Route>
                <Route exact path={appRoutes.categoriesPath()} component={Categories} />
                <Route exact path={appRoutes.categoryPath(":slug")} component={Category} />
                <Route exact path={appRoutes.invitesPath()} component={InvitesLandingPage} />
                <Route exact path={appRoutes.starringYouAIPath()} component={StarringYouAILandingPage} />
                <Route exact path={appRoutes.invitePath(":slug")} component={InviteCategory} />
                <Route
                  exact
                  path={appRoutes.inviteSubcategoryPath(":slug", ":subcategory")}
                  component={InviteCategory}
                />
                <Route path={appRoutes.templateGroupPath(":slug")} component={TemplateGroup} />
                <Route path={appRoutes.newCastPath(":slug")} component={NewCast} />
                <Route path={appRoutes.facePath()} component={Face} />
                <Route exact path={appRoutes.termsOfSalePath()} component={TermsOfSale} />
                <Route exact path={appRoutes.termsOfServicePath()} component={TermsOfService} />
                <Route exact path={appRoutes.privacyPath()} component={Privacy} />
                <Route exact path={appRoutes.legalPath()} component={Legal} />
                <Route exact path={appRoutes.aboutUsPath()} component={AboutUs} />
                <Route path={appRoutes.page404Path()} component={Page404} />
                <Route path={appRoutes.makeViewPath(":slug", ":id")} component={MakeView} />
                <Route path={appRoutes.makePreviewPath(":slug", ":id")} component={MakePreview} />
                <Route exact path={appRoutes.unauthorizedPath()} component={Unauthorized} />
                <Route path={appRoutes.loginPath()} component={Login} />
                <Route exact path={appRoutes.signUpPath()} component={SignUp} />
                <Route path={appRoutes.resetPasswordPath()} component={ResetPassword} />
                <Route path={appRoutes.resetPasswordReturnPath(":token")} component={ResetPasswordReturn} />
                <Route path={appRoutes.loginHelpPath()} component={LoginHelp} />
                <ProtectedRoute path={appRoutes.myAccountPath()} component={Overview} />
                <ProtectedRoute path={appRoutes.changeNamePath()} component={ChangeName} />
                <ProtectedRoute path={appRoutes.changeEmailPath()} component={ChangeEmail} />
                <ProtectedRoute path={appRoutes.changePasswordPath()} component={ChangePassword} />
                <ProtectedRoute path={appRoutes.receiptsPath()} component={Receipts} />
                <ProtectedRoute path={appRoutes.receiptPath(":id")} component={Receipt} />
                <ProtectedRoute path={appRoutes.subscriptionDetailsPath()} component={SubscriptionDetails} />
                <ProtectedRoute path={appRoutes.editRenewalPath()} component={EditRenewal} />
                <ProtectedRoute path={appRoutes.myEcardsPath()} component={MyEcards} />
                <Route path={appRoutes.manageInvitePath(":slug")} component={ManageInvite} />
                <ProtectedRoute path={appRoutes.myInvitesPath()} component={MyInvites} />
                <ProtectedRoute path={appRoutes.peoplePath()} component={People} />
                <ProtectedRoute path={appRoutes.paypalConfirmationPath()} component={PaypalConfirmation} />
                <Route path={appRoutes.updateCreditCardPath()} component={UpdateCreditCard} />
                <Route path={appRoutes.purchaseConfirmationPath()} component={PurchaseConfirmation} />
                <Route path={appRoutes.paymentPath()} component={Payment} />
                <Route path={appRoutes.morphablePackGroupPath(":slug")} component={MorphablePackGroup} />
                <Route path={appRoutes.invitesPreviewPath(":slug")} component={InvitesPreview} />
                <Route path={appRoutes.invitesCastingPath(":slug")} component={InvitesCasting} />
                <Route path={appRoutes.inviteMakePreviewPath(":slug")} component={InviteMakePreview} />
                <Route path={appRoutes.inviteGuestViewPath(":tg", ":invite", ":guest")} component={InviteGuestView} />
                <Route path={appRoutes.invitePublicViewPath(":invite")} component={InvitePublicView} />
                <Route path={appRoutes.syaiPackPreviewPath(":slug")} component={SYAIPackPreview} />
                <Route path={appRoutes.syaiPhotoConfirmationPath()} component={SYAIPhotoConfirmation} />
                <Route path={appRoutes.syaiPackViewPath(":slug")} component={SYAIPackView} />
                <ProtectedRoute path={appRoutes.syaiMyPacksPath()} component={SYAIMyPacks} />
                <Route path={appRoutes.algoliaTestPath()} component={AlgoliaTestPage} />
                <Redirect to={{ pathname: appRoutes.page404Path(), state: { referrer: location.pathname } }} />
              </Switch>
            </UserLayout>
          </Route>
        </Switch>
      </FeatureFlags>
    </Suspense>
  );
};

const CookieBanner = lazy(() => import("components/CookieBanner"));

const UserApp = () => {
  useRefreshAuthToken();

  const { changeValue, changeLoading, removeValue } = usePageUpdated();

  useEffect(() => {
    registerServiceWorker(
      () => changeValue(true),
      () => changeLoading(true),
      () => removeValue(),
    );
  }, []);

  return (
    <ThemeProvider>
      <SnackbarProvider maxSnack={3}>
        <ExperimentProvider>
          <FaceOrgProvider>
            <InAppBrowserIntentProvider>
              <Router history={history}>
                <Suspense fallback={<span />}>
                  <RenderModal />
                  <CookieBanner />
                </Suspense>
                <Routes />
              </Router>
            </InAppBrowserIntentProvider>
          </FaceOrgProvider>
        </ExperimentProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

UserApp.propTypes = {};

Routes.propTypes = {};

export default UserApp;
